import { useState } from "react";
import { Plus, Search, LayoutGrid, List, ArrowUpDown, X, Edit, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link, useLocation } from "wouter";
import { ClientGroupFormModal } from "@/components/client-group-form-modal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClientGroup } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type SortField = "groupName" | "createdAt" | "updatedAt";
type SortDirection = "asc" | "desc";
type ViewMode = "card" | "table";

export default function ClientGroupList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [sortField, setSortField] = useState<SortField>("groupName");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [editingGroup, setEditingGroup] = useState<ClientGroup | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<ClientGroup | null>(null);

  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: clientGroups = [], isLoading } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // Filter and sort
  const filteredGroups = clientGroups.filter((group) =>
    group.groupName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayGroups = [...filteredGroups].sort((a, b) => {
    let aValue: string | Date;
    let bValue: string | Date;

    switch (sortField) {
      case "groupName":
        aValue = a.groupName.toLowerCase();
        bValue = b.groupName.toLowerCase();
        break;
      case "createdAt":
        aValue = new Date(a.createdAt || 0);
        bValue = new Date(b.createdAt || 0);
        break;
      case "updatedAt":
        aValue = new Date(a.updatedAt || 0);
        bValue = new Date(b.updatedAt || 0);
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Action handlers
  const handleEdit = (group: ClientGroup) => {
    setEditingGroup(group);
    setIsModalOpen(true);
  };

  const handleDelete = (group: ClientGroup) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };

  const handleView = (groupId: string) => {
    setLocation(`/client-groups/${groupId}`);
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      await apiRequest('DELETE', `/api/crm/client-groups/${groupToDelete.id}`);

      toast({
        title: "Client group deleted",
        description: `${groupToDelete.groupName} has been deleted successfully.`,
      });

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["/api/crm/client-groups"] });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete client group",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
    }
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingGroup(null);
    }
  };

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Client Groups"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Client Groups" }]}
        actions={
          <Button
            onClick={() => setIsModalOpen(true)}
            data-testid="button-add-client-group"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Client Group
          </Button>
        }
      />

      <ClientGroupFormModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        clientGroup={editingGroup ?? undefined}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Group</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{groupToDelete?.groupName}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <main className="flex-1 overflow-auto bg-muted/20 p-4">
        <div className="mx-auto max-w-7xl space-y-4">
          <Card className="p-3">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search client groups..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                  data-testid="input-search-client-groups"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <div className="flex border rounded-md">
                  <Button
                    variant={viewMode === "table" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("table")}
                    className="rounded-r-none"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === "card" ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setViewMode("card")}
                    className="rounded-l-none"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading client groups...</p>
            </div>
          ) : displayGroups.length > 0 ? (
            viewMode === "card" ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {displayGroups.map((group) => (
                  <Link key={group.id} href={`/client-groups/${group.id}`}>
                    <Card className="hover-elevate cursor-pointer h-full" data-testid={`card-client-group-${group.id}`}>
                      <CardHeader>
                        <CardTitle className="flex items-start justify-between">
                          <span className="text-lg">{group.groupName}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {group.description || "No description"}
                        </p>
                        <div className="text-xs text-muted-foreground pt-2 border-t">
                          Created {new Date(group.createdAt).toLocaleDateString("en-AU")}
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow className="h-10">
                      <TableHead className="px-2 py-1 text-xs font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("groupName")}
                        >
                          Group Name
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">Description</TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("createdAt")}
                        >
                          Created
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hover:bg-transparent"
                          onClick={() => toggleSort("updatedAt")}
                        >
                          Updated
                          <ArrowUpDown className="ml-2 h-3 w-3" />
                        </Button>
                      </TableHead>
                      <TableHead className="px-2 py-1 text-xs font-medium text-right w-[140px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayGroups.map((group) => (
                      <TableRow
                        key={group.id}
                        className="h-10 hover:bg-muted/50"
                        data-testid={`row-client-group-${group.id}`}
                      >
                        <TableCell className="px-2 py-1 text-sm font-medium">
                          <Link href={`/client-groups/${group.id}`}>
                            <span className="hover:underline cursor-pointer">
                              {group.groupName}
                            </span>
                          </Link>
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground truncate max-w-[300px]">
                          {group.description || "-"}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {format(new Date(group.createdAt), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="px-2 py-1 text-sm text-muted-foreground">
                          {format(new Date(group.updatedAt), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="px-2 py-1">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(group)}
                              data-testid={`button-edit-client-group-${group.id}`}
                              title="Edit client group"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(group)}
                              data-testid={`button-delete-client-group-${group.id}`}
                              className="text-destructive hover:text-destructive"
                              title="Delete client group"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(group.id)}
                              data-testid={`button-view-client-group-${group.id}`}
                              title="View details"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? "No client groups found matching your search" : "No client groups yet"}
                </p>
                {!searchTerm && (
                  <Button onClick={() => setIsModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Client Group
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
